// useShoppingCart  customHook ref:simple

import { CartProduct } from "@artisan-commerce/types";
import { AdditionalInfo } from "@artisan-commerce/types";
import { useSendEventAddToCartMasivo } from "@multicines/services";
import { useShoppingCartStore } from "@multicines/stores";
import { useMasivoStore } from "@multicines/stores";
import { sanitizeForFirestore } from "@multicines/utils";
import { addProduct, deleteShoppingCart } from "artisn/shopping-cart";
import { listenShoppingCart } from "artisn/shopping-cart";
import { removeProduct, replaceProduct } from "artisn/shopping-cart";
import { setProduct } from "artisn/shopping-cart";
import { updateShoppingCart } from "artisn/shopping-cart";
import { createShoppingCart, getShoppingCart } from "artisn/shopping-cart";
import { useCallback, useMemo } from "react";

import { UseShoppingCartValues } from "./useShoppingCart.types";
import CONSTANTS from "config/constants";
import { useAuthStore } from "stores/auth/auth.store";
import { createErrorNotification } from "utils/notifications.utils";
import { dismissErrorNotification } from "utils/notifications.utils";

const { ACCOUNT_ID } = CONSTANTS.ARTISN;

const useShoppingCart = ():
  | UseShoppingCartValues
  | Record<keyof UseShoppingCartValues, undefined> => {
  const isAnonymous = useAuthStore(state => state.isAnonymous);
  const uid = useAuthStore(state => state.uid);
  const setShoppingCart = useShoppingCartStore(store => store.setShoppingCart);
  const addCartMasivoQuery = useSendEventAddToCartMasivo();
  const addToCartSent = useMasivoStore(store => store.addToCartSent);
  const setAddToCartSent = useMasivoStore(store => store.setAddToCartSent);

  const { mutateAsync: sendEventAddToCartMasivo } = addCartMasivoQuery;
  const { reset: resetSendEventAddToCartMasivo } = addCartMasivoQuery;

  const sendAddToCartMasivo = useCallback(
    async (product: CartProduct<AdditionalInfo, AdditionalInfo>) => {
      if (isAnonymous) return;
      if (addToCartSent) return;
      try {
        await sendEventAddToCartMasivo(
          product as CartProduct<AdditionalInfo, AdditionalInfo>
        );
        resetSendEventAddToCartMasivo();
        setAddToCartSent(true);
      } catch (error) {
        const { message } = error as Error;
        createErrorNotification(
          message,
          undefined,
          dismissErrorNotification,
          1000 * 30
        );
      }
    },
    [
      addToCartSent,
      isAnonymous,
      resetSendEventAddToCartMasivo,
      sendEventAddToCartMasivo,
      setAddToCartSent
    ]
  );

  const config = useMemo(() => {
    if (!uid) return null;
    if (typeof isAnonymous === "undefined") return null;

    return {
      accountId: ACCOUNT_ID,
      anonymous: isAnonymous,
      customerId: uid
    };
  }, [isAnonymous, uid]);

  return useMemo(() => {
    if (!config) return {};

    return {
      listenShoppingCart: () => {
        return listenShoppingCart(cart => {
          if (cart?.id === "" || cart?.id === null) return;
          setShoppingCart(cart ?? undefined);
        }, config);
      },
      createShoppingCart: (options, overrides) => {
        const newConfig = {
          ...config,
          ...options
        };
        return createShoppingCart(newConfig, overrides);
      },
      getShoppingCart: options => {
        const newConfig = {
          ...config,
          ...options
        };
        return getShoppingCart(newConfig);
      },
      deleteShoppingCart: options => {
        const newConfig = {
          ...config,
          ...options
        };
        return deleteShoppingCart(newConfig);
      },
      updateShoppingCart: cb => {
        return updateShoppingCart(prev => {
          const updatedCart = cb(prev);
          return sanitizeForFirestore(updatedCart);
        }, config);
      },
      addProduct: async (product, options) => {
        const { amount } = options;
        await sendAddToCartMasivo({
          ...(product as CartProduct<AdditionalInfo, AdditionalInfo>),
          ...(amount ? { amount } : {})
        });
        const newConfig = {
          ...config,
          ...options
        };
        return addProduct(product, newConfig);
      },
      removeProduct: (product, options) => {
        const newConfig = {
          ...config,
          ...options
        };
        return removeProduct(product, newConfig);
      },
      setProduct: (product, options) => {
        const newConfig = {
          ...config,
          ...options
        };
        return setProduct(product, newConfig);
      },
      replaceProduct: (product, options) => {
        const newConfig = {
          ...config,
          ...options
        };
        return replaceProduct(product, newConfig);
      }
    };
  }, [config, sendAddToCartMasivo, setShoppingCart]);
};

export default useShoppingCart;
